import {useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {type AppBskyActorDefs, type AppBskyFeedDefs} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import * as bcp47Match from 'bcp-47-match'

import {sanitizeHandle} from '#/lib/strings/handles'
import {isSafari} from '#/lib/browser'
import {useLanguagePrefs} from '#/state/preferences/languages'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useGetSuggestedFeedsQuery} from '#/state/queries/trending/useGetSuggestedFeedsQuery'
import {useSession} from '#/state/session'
import {atoms as a, select, useTheme, web} from '#/alf'
import {isWeb} from '#/platform/detection'
import {LoadingPlaceholder} from '#/view/com/util/LoadingPlaceholder'
import * as FeedCard from '#/components/FeedCard'
import {Link} from '#/components/Link'
import * as ProfileCard from '#/components/ProfileCard'
import {Text} from '#/components/Typography'
import {FeedSaveButton} from './ExploreFeedActions'
import {useSuggestedUsers} from '../util/useSuggestedUsers'

const MAX_PROFILES = 5
const MAX_FEEDS = 5
const CTA_WIDTH_ACCOUNTS = 96
const CTA_WIDTH_FEEDS = 112

export function ExploreRightRail() {
  const t = useTheme()
  const {_} = useLingui()
  const {hasSession} = useSession()
  const moderationOpts = useModerationOpts()
  const {contentLanguages} = useLanguagePrefs()
  const useFullExperience = useMemo(() => {
    // Safari language prefs often omit "en", which hides the full Explore modules.
    if (isWeb && isSafari) return true
    if (contentLanguages.length === 0) return true
    return bcp47Match.basicFilter('en', contentLanguages).length > 0
  }, [contentLanguages])

  const {
    data: suggestedUsers,
    isLoading: isLoadingUsers,
    isRefetching: isRefetchingUsers,
  } = useSuggestedUsers({
    category: null,
    search: !useFullExperience,
  })

  const {data: suggestedFeeds, isLoading: isLoadingFeeds} =
    useGetSuggestedFeedsQuery({
      enabled: useFullExperience,
    })

  const profileItems = useMemo(() => {
    if (!suggestedUsers?.actors || !moderationOpts) return []
    const seen = new Set<string>()
    return suggestedUsers.actors
      .filter(actor => !actor.viewer?.following)
      .filter(actor => {
        if (seen.has(actor.did)) return false
        seen.add(actor.did)
        return true
      })
      .slice(0, MAX_PROFILES)
  }, [moderationOpts, suggestedUsers?.actors])

  const feedItems = useMemo(() => {
    if (!suggestedFeeds?.feeds) return []
    const seen = new Set<string>()
    return suggestedFeeds.feeds
      .filter(feed => {
        if (seen.has(feed.uri)) return false
        seen.add(feed.uri)
        return true
      })
      .slice(0, MAX_FEEDS)
  }, [suggestedFeeds?.feeds])

  if (!hasSession) return null

  return (
    <View style={[a.gap_md]}>
      <RightRailCard title={_(msg`Suggested accounts`)}>
        {isLoadingUsers || isRefetchingUsers ? (
          <RightRailProfileSkeletons />
        ) : profileItems.length > 0 ? (
          profileItems.map(profile => (
            <SuggestedAccountRow
              key={profile.did}
              profile={profile}
              moderationOpts={moderationOpts}
            />
          ))
        ) : (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {_(msg`No suggestions right now.`)}
          </Text>
        )}
      </RightRailCard>

      {(isLoadingFeeds || feedItems.length > 0) && (
        <RightRailCard title={_(msg`Recommended feeds`)}>
          {isLoadingFeeds ? (
            <RightRailFeedSkeletons />
          ) : (
            feedItems.map(feed => (
              <SuggestedFeedRow key={feed.uri} feed={feed} />
            ))
          )}
        </RightRailCard>
      )}
    </View>
  )
}

function RightRailCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  const t = useTheme()
  const glassBackground = select(t.name, {
    light: 'rgba(255,255,255,0.86)',
    dark: 'rgba(18,18,18,0.7)',
    dim: 'rgba(18,18,18,0.72)',
  })
  const glassBorder = select(t.name, {
    light: 'rgba(0,0,0,0.06)',
    dark: 'rgba(255,255,255,0.08)',
    dim: 'rgba(255,255,255,0.08)',
  })

  return (
    <View
      style={[
        a.rounded_lg,
        a.p_lg,
        a.gap_md,
        {
          borderWidth: StyleSheet.hairlineWidth,
          backgroundColor: glassBackground,
          borderColor: glassBorder,
        },
        a.shadow_sm,
        web({
          boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }),
      ]}>
      <Text style={[a.text_md, a.font_bold]}>{title}</Text>
      <View style={[a.gap_sm]}>{children}</View>
    </View>
  )
}

function SuggestedAccountRow({
  profile,
  moderationOpts,
}: {
  profile: AppBskyActorDefs.ProfileView
  moderationOpts: NonNullable<ReturnType<typeof useModerationOpts>>
}) {
  const t = useTheme()

  return (
    <ProfileCard.Link profile={profile} style={[a.w_full]}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.flex_row,
            a.align_center,
            a.gap_md,
            a.py_sm,
            a.rounded_md,
            (hovered || pressed) && t.atoms.bg_contrast_25,
          ]}>
          <ProfileCard.Avatar
            profile={profile}
            moderationOpts={moderationOpts}
            size={32}
          />
          <View style={[a.flex_1, {minWidth: 0, alignItems: 'flex-start'}]}>
            <ProfileCard.NameAndHandle
              profile={profile}
              moderationOpts={moderationOpts}
              inline={false}
            />
          </View>
          <View
            style={[
              {width: CTA_WIDTH_ACCOUNTS, alignItems: 'flex-end', flexShrink: 0},
            ]}>
            <ProfileCard.FollowButton
              profile={profile}
              moderationOpts={moderationOpts}
              withIcon={false}
              logContext="ExploreSuggestedAccounts"
              size="small"
              style={{width: CTA_WIDTH_ACCOUNTS, flexShrink: 0}}
            />
          </View>
        </View>
      )}
    </ProfileCard.Link>
  )
}

function SuggestedFeedRow({feed}: {feed: AppBskyFeedDefs.GeneratorView}) {
  const t = useTheme()
  const {_} = useLingui()
  const href = useMemo(() => {
    return FeedCard.createProfileFeedHref({feed})
  }, [feed])

  return (
    <Link label={feed.displayName} to={href} style={[a.w_full]}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.flex_row,
            a.align_center,
            a.gap_md,
            a.py_sm,
            a.rounded_md,
            (hovered || pressed) && t.atoms.bg_contrast_25,
          ]}>
          <FeedCard.Avatar src={feed.avatar} size={32} />
          <View style={[a.flex_1, {minWidth: 0, alignItems: 'flex-start'}]}>
            <Text style={[a.text_sm, a.font_bold]} numberOfLines={1}>
              {feed.displayName}
            </Text>
            <Text
              style={[a.text_xs, t.atoms.text_contrast_medium]}
              numberOfLines={1}>
              {_(msg`By ${sanitizeHandle(feed.creator.handle || feed.creator.did, '@')}`)}
            </Text>
          </View>
          <View
            style={[
              {width: CTA_WIDTH_FEEDS, alignItems: 'flex-end', flexShrink: 0},
            ]}>
            <FeedSaveButton
              feed={feed}
              size="small"
              style={{width: CTA_WIDTH_FEEDS, flexShrink: 0}}
            />
          </View>
        </View>
      )}
    </Link>
  )
}

function RightRailProfileSkeletons() {
  const t = useTheme()
  return (
    <View style={[a.gap_md]}>
      {Array.from({length: 3}).map((_, index) => (
        <View
          key={`profile-skeleton-${index}`}
          style={[
            a.flex_row,
            a.align_center,
            a.gap_md,
            a.py_sm,
            a.rounded_md,
            t.atoms.bg_contrast_25,
          ]}>
          <View
            style={[
              a.rounded_full,
              t.atoms.bg_contrast_50,
              {width: 32, height: 32},
            ]}
          />
          <View style={[a.flex_1, a.gap_xs]}>
            <LoadingPlaceholder width={120} height={12} />
            <LoadingPlaceholder width={80} height={10} />
          </View>
        </View>
      ))}
    </View>
  )
}

function RightRailFeedSkeletons() {
  const t = useTheme()
  return (
    <View style={[a.gap_md]}>
      {Array.from({length: 3}).map((_, index) => (
        <View
          key={`feed-skeleton-${index}`}
          style={[
            a.flex_row,
            a.align_center,
            a.gap_md,
            a.py_sm,
            a.rounded_md,
            t.atoms.bg_contrast_25,
          ]}>
          <View
            style={[
              a.rounded_sm,
              t.atoms.bg_contrast_50,
              {width: 32, height: 32},
            ]}
          />
          <View style={[a.flex_1, a.gap_xs]}>
            <LoadingPlaceholder width={120} height={12} />
            <LoadingPlaceholder width={90} height={10} />
          </View>
        </View>
      ))}
    </View>
  )
}
